import axios from 'axios'
import { getAngelEnvironment } from '@/utils/environment-utils'
import { getBffBaseUrl } from '@/utils/environment-variables'
import { logger } from '@/utils/logging'

export type CacheKey = `${string}_${string}_${string}`

export interface CacheOptions {
  expireInSeconds?: number
}

async function get(key: CacheKey): Promise<string | null> {
  try {
    const baseUrl = getBffBaseUrl()
    const response = await axios.get(`${baseUrl}/cache`, { params: { key } })
    return response.data.value
  } catch (err) {
    logger().error('Unexpected error in cacheService.get', { key, err })
    return null
  }
}

async function mGet(keys: CacheKey[]): Promise<Record<CacheKey, string | null>> {
  try {
    const baseUrl = getBffBaseUrl()
    const response = await axios.post(`${baseUrl}/cache/multi/get`, { keys })
    return response.data.keyValueMap
  } catch (err) {
    logger().error('Unexpected error in cacheService.mGet', { keys, err })
    return {}
  }
}

async function getParsed<T>(key: CacheKey): Promise<T | null> {
  try {
    const value = await get(key)
    if (!value) return null
    return JSON.parse(value) as T
  } catch (err) {
    logger().error('Unexpected error in cacheService.getParsed', { key, err })
    return null
  }
}

async function set(key: CacheKey, value: string, opts?: CacheOptions): Promise<void> {
  const options = validateOptions(opts)
  try {
    const baseUrl = getBffBaseUrl()
    await axios.post(`${baseUrl}/cache`, { key, value, options })
  } catch (err) {
    logger().error('Unexpected error in cacheService.set', { key, value, options, err })
  }
}

async function mSet(keyValueMap: Record<CacheKey, string>, opts?: CacheOptions): Promise<void> {
  const options = validateOptions(opts)
  try {
    const baseUrl = getBffBaseUrl()
    await axios.post(`${baseUrl}/cache/multi/set`, { keyValueMap, options })
  } catch (err) {
    logger().error('Unexpected error in cacheService.mSet', { keyValueMap, err })
  }
}

function validateOptions(options: CacheOptions | undefined): CacheOptions | undefined {
  if (!options) return

  const validOptions: CacheOptions = {}
  // ensure that expireInSeconds is an integer
  if (options.expireInSeconds) validOptions.expireInSeconds = Math.round(options.expireInSeconds)

  return validOptions
}

export function genCacheKey(moduleName: string, descriptor: string): CacheKey {
  const potentialKey: CacheKey = `${getAngelEnvironment()}_${moduleName}_${descriptor}`
  const hasInvalidChars = potentialKey.match(/[^a-zA-Z0-9._-]/)
  if (hasInvalidChars) {
    throw new Error(`Invalid characters used in cache key: ${potentialKey}`)
  }
  if (potentialKey.length > 255) {
    throw new Error(`Cache key is too long, the key must be <256 characters: ${potentialKey}`)
  }
  return potentialKey
}

export const cacheService = { genCacheKey, get, mGet, getParsed, set, mSet }
